import { useEffect, useMemo, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  applyFleetOnboardingRouteContext,
  createInitialFleetImportBridgeDraft,
  FLEET_VERTICAL_LABELS,
  type FleetOnboardingSearch,
  type FleetVertical,
  resolveFleetOnboardingVertical,
  summarizeFleetOnboardingDraft,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import { FleetOnboardingStepper } from "@/features/operator/fleet/fleet-onboarding-stepper"
import {
  OperatorEmptyState,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  isApiError,
  type FleetOnboardingUploadResponse,
} from "@/lib/api/client"

type CsvRow = Record<string, string>

interface CsvPreviewResult {
  errors: string[]
  headers: string[]
  rows: CsvRow[]
  warnings: string[]
}

const BRIDGE_SUPPORTED_EXTENSIONS = [".csv", ".txt"] as const

const BRIDGE_REQUIRED_ASSET_HEADERS = [
  ["assetNumber", "asset_number", "plateNumber", "plate_number", "externalRef", "external_ref"],
  ["operatingUnitCode", "operating_unit_code", "branchCode", "branch_code", "zoneCode", "zone_code", "routeCode", "route_code"],
] as const

const BRIDGE_OPTIONAL_MASTER_HEADERS = [
  "fleetName",
  "fleet_name",
  "companyName",
  "company_name",
  "contactName",
  "contact_name",
  "contactEmail",
  "contact_email",
  "contactPhone",
  "contact_phone",
  "assigneeName",
  "assignee_name",
  "assigneeEmail",
  "assignee_email",
  "assigneePhone",
  "assignee_phone",
  "assignmentStatus",
  "assignment_status",
  "vehicleType",
  "vehicle_type",
  "vehicleMake",
  "vehicle_make",
  "vehicleModel",
  "vehicle_model",
  "vehicleYear",
  "vehicle_year",
  "vehicleColor",
  "vehicle_color",
  "branchCode",
  "branch_code",
  "operatingUnitCode",
  "operating_unit_code",
  "routeCode",
  "route_code",
  "zoneCode",
  "zone_code",
  "notes",
] as const

function parseCsvText(csvText: string): CsvPreviewResult {
  const text = String(csvText || "").replace(/^\uFEFF/, "")
  if (!text.trim()) {
    return {
      errors: ["CSV content is empty."],
      headers: [],
      rows: [],
      warnings: [],
    }
  }

  const rows: string[][] = []
  let currentField = ""
  let currentRow: string[] = []
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    const nextChar = text[index + 1]

    if (char === "\"") {
      if (inQuotes && nextChar === "\"") {
        currentField += "\""
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === "," && !inQuotes) {
      currentRow.push(currentField.trim())
      currentField = ""
      continue
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") {
        index += 1
      }
      currentRow.push(currentField.trim())
      currentField = ""
      if (currentRow.some((value) => value.length > 0)) {
        rows.push(currentRow)
      }
      currentRow = []
      continue
    }

    currentField += char
  }

  currentRow.push(currentField.trim())
  if (currentRow.some((value) => value.length > 0)) {
    rows.push(currentRow)
  }

  if (rows.length < 2) {
    return {
      errors: ["CSV file must include a header row and at least one data row."],
      headers: rows[0] ?? [],
      rows: [],
      warnings: [],
    }
  }

  const [headerRow, ...dataRows] = rows
  const headers = headerRow.map((value) => value.trim())
  const normalizedHeaderSet = new Set(headers.map((header) => header.toLowerCase()))
  const errors: string[] = []
  const warnings: string[] = []

  for (const requiredHeaderSet of BRIDGE_REQUIRED_ASSET_HEADERS) {
    const found = requiredHeaderSet.some((candidate) =>
      normalizedHeaderSet.has(candidate.toLowerCase())
    )

    if (!found) {
      const missingGroupLabel = requiredHeaderSet.some((candidate) =>
        candidate.toLowerCase().includes("operating") || candidate.toLowerCase().includes("branch")
      )
        ? "operating unit code"
        : "asset identifier"
      errors.push(
        `CSV must include at least one ${missingGroupLabel} column: ${requiredHeaderSet.join(", ")}`
      )
    }
  }

  const rowObjects: CsvRow[] = dataRows.map((values) => {
    const row: CsvRow = {}
    headers.forEach((header, headerIndex) => {
      row[header] = values[headerIndex] ?? ""
    })
    return row
  })

  const assetValues = rowObjects.map((row) =>
    row.assetNumber
    || row.asset_number
    || row.plateNumber
    || row.plate_number
    || row.externalRef
    || row.external_ref
    || ""
  )
  const missingAssetRows = assetValues
    .map((value, index) => ({ index: index + 2, value }))
    .filter((entry) => !entry.value.trim())

  if (missingAssetRows.length > 0) {
    errors.push(
      `Asset identifier is missing in ${missingAssetRows.length} row(s). Add assetNumber, plateNumber, or externalRef.`
    )
  }

  const operatingUnitValues = rowObjects.map((row) =>
    row.operatingUnitCode
    || row.operating_unit_code
    || row.branchCode
    || row.branch_code
    || row.zoneCode
    || row.zone_code
    || row.routeCode
    || row.route_code
    || ""
  )
  const missingOperatingUnitRows = operatingUnitValues
    .map((value, index) => ({ index: index + 2, value }))
    .filter((entry) => !entry.value.trim())

  if (missingOperatingUnitRows.length > 0) {
    errors.push(
      `Operating unit code is missing in ${missingOperatingUnitRows.length} row(s). Add operatingUnitCode, branch_code, zone_code, or route_code.`
    )
  }

  const duplicateAssets = new Set<string>()
  const seenAssets = new Set<string>()
  for (const value of assetValues) {
    const normalized = value.trim().toUpperCase()
    if (!normalized) {
      continue
    }
    if (seenAssets.has(normalized)) {
      duplicateAssets.add(normalized)
    } else {
      seenAssets.add(normalized)
    }
  }

  if (duplicateAssets.size > 0) {
    errors.push(
      `Duplicate asset identifiers detected: ${Array.from(duplicateAssets).slice(0, 5).join(", ")}`
    )
  }

  const unsupportedHeaders = headers.filter((header) => {
    const normalized = header.toLowerCase()
    const knownOptional = BRIDGE_OPTIONAL_MASTER_HEADERS.some(
      (candidate) => candidate.toLowerCase() === normalized
    )
    const knownRequired = BRIDGE_REQUIRED_ASSET_HEADERS.some((requiredHeaderSet) =>
      requiredHeaderSet.some((candidate) => candidate.toLowerCase() === normalized)
    )

    return !knownOptional && !knownRequired
  })

  if (unsupportedHeaders.length > 0) {
    warnings.push(
      `Some columns are not yet used by the current Fleet CSV bridge: ${unsupportedHeaders.join(", ")}`
    )
  }

  return {
    errors,
    headers,
    rows: rowObjects,
    warnings,
  }
}

function countDistinct(values: Array<string | undefined>) {
  return new Set(values.map((value) => value?.trim()).filter(Boolean)).size
}

function buildBridgeCsvTemplate(vertical: FleetVertical) {
  const routeHeader =
    vertical === "two_wheeler_delivery"
      ? "zone_code"
      : vertical === "intercity_bus"
        ? "route_code"
        : "branch_code"

  const vehicleType =
    vertical === "two_wheeler_delivery"
      ? "two_wheeler"
      : vertical === "intercity_bus"
        ? "bus"
        : "truck"

  return [
    [
      "plateNumber",
      "fleetName",
      "companyName",
      "contactName",
      "contactEmail",
      "contactPhone",
      "assigneeName",
      "assigneePhone",
      "assignmentStatus",
      "vehicleType",
      "vehicleMake",
      "vehicleModel",
      routeHeader,
      "notes",
    ].join(","),
    [
      "KA01AB1234",
      "South India Corridor",
      "Grobet Logistics Network",
      "Fleet Ops",
      "ops@grobet.example",
      "+919900001111",
      "Driver One",
      "+919900001222",
      "assigned",
      vehicleType,
      "Tata",
      "Ultra",
      vertical === "two_wheeler_delivery"
        ? "BLR-NORTH"
        : vertical === "intercity_bus"
          ? "BLR-HYD-01"
          : "BLR-HQ",
      "Bridge import sample row",
    ].join(","),
  ].join("\n")
}

interface FleetOnboardingImportPageProps {
  onContinueToReview: () => void
  search: FleetOnboardingSearch
}

export function FleetOnboardingImportPage({
  onContinueToReview,
  search,
}: FleetOnboardingImportPageProps) {
  const { draft, setDraft } = useFleetOnboardingDraft()
  const summary = summarizeFleetOnboardingDraft(draft)
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [csvText, setCsvText] = useState("")
  const [isParsing, setIsParsing] = useState(false)
  const [isSavingUpload, setIsSavingUpload] = useState(false)
  const [preview, setPreview] = useState<CsvPreviewResult | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitResult, setSubmitResult] = useState<FleetOnboardingUploadResponse | null>(null)

  useEffect(() => {
    setDraft((previous) =>
      applyFleetOnboardingRouteContext(previous, {
        mode: search.mode,
        organizationId: search.organizationId,
        runId: search.runId,
        vertical: search.vertical,
      })
    )
  }, [search.mode, search.organizationId, search.runId, search.vertical, setDraft])

  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)
  const canContinueToReview =
    Boolean(draft.importBridge?.lastDryRunAt)
    && (draft.importBridge?.blockingErrors.length ?? 0) === 0
    && (draft.importBridge?.dryRunFailed ?? 0) === 0
    && (draft.importBridge?.dryRunTotal ?? 0) > 0

  const previewSummary = useMemo(() => {
    if (!preview) {
      return null
    }

    return {
      assetCount: preview.rows.length,
      assignmentCount: preview.rows.filter((row) =>
        Boolean((row.assigneeName || row.assignee_name || "").trim())
      ).length,
      operatingUnitCount: countDistinct(
        preview.rows.map((row) =>
          row.operatingUnitCode
          || row.operating_unit_code
          || row.branchCode
          || row.branch_code
          || row.zoneCode
          || row.zone_code
          || row.routeCode
          || row.route_code
        )
      ),
      peopleCount: countDistinct(
        preview.rows.flatMap((row) => [row.assigneeName, row.assignee_name, row.contactName, row.contact_name])
      ),
    }
  }, [preview])

  async function handleFileSelection(file: File | null) {
    setSubmitResult(null)
    setSubmitError(null)

    if (!file) {
      setSelectedFileName(null)
      setCsvText("")
      setPreview(null)
      setDraft((previous) => ({
        ...previous,
        importBridge: null,
        kitIssuance: null,
        masterPersistence: null,
      }))
      return
    }

    setSelectedFileName(file.name)
    setIsParsing(true)

    try {
      const fileText = await file.text()
      const nextPreview = parseCsvText(fileText)
      setCsvText(fileText)
      setPreview(nextPreview)
      setDraft((previous) => ({
        ...previous,
        importBridge: {
          ...createInitialFleetImportBridgeDraft(),
          assetCount: nextPreview.rows.length,
          assignmentCount: nextPreview.rows.filter((row) =>
            Boolean((row.assigneeName || row.assignee_name || "").trim())
          ).length,
          blockingErrors: nextPreview.errors,
          fileName: file.name,
          headers: nextPreview.headers,
          operatingUnitHintCount: countDistinct(
            nextPreview.rows.map(
              (row) =>
                row.branchCode
                || row.branch_code
                || row.operatingUnitCode
                || row.operating_unit_code
                || row.zoneCode
                || row.zone_code
                || row.routeCode
                || row.route_code
            )
          ),
          peopleCount: countDistinct(
            nextPreview.rows.flatMap((row) => [
              row.assigneeName,
              row.assignee_name,
              row.contactName,
              row.contact_name,
            ])
          ),
          sourceRows: nextPreview.rows,
          warnings: nextPreview.warnings,
        },
        kitIssuance: null,
        masterPersistence: null,
      }))
    } catch (error) {
      const fallbackPreview = {
        errors: [
          error instanceof Error ? error.message : "The selected file could not be read.",
        ],
        headers: [],
        rows: [],
        warnings: [],
      } satisfies CsvPreviewResult
      setPreview(fallbackPreview)
      setDraft((previous) => ({
        ...previous,
        importBridge: {
          ...createInitialFleetImportBridgeDraft(),
          blockingErrors: fallbackPreview.errors,
          fileName: file.name,
        },
        kitIssuance: null,
        masterPersistence: null,
      }))
    } finally {
      setIsParsing(false)
    }
  }

  async function saveUploadRows() {
    if (!csvText.trim()) {
      setSubmitError("Choose a CSV file before saving onboarding upload rows.")
      return
    }

    if (preview?.errors.length) {
      setSubmitError("Resolve the blocking CSV errors before saving upload rows.")
      return
    }

    setIsSavingUpload(true)
    setSubmitError(null)
    setSubmitResult(null)

    try {
      const resolvedRunId =
        search.runId
        ?? draft.activeRunId
        ?? draft.masterPersistence?.runId
        ?? null

      const result = await apiClient.uploadFleetOnboardingRows({
        fileName: selectedFileName,
        organization: {
          city: draft.primaryCity || null,
          country: draft.primaryCountry || null,
          fleetAdminEmail: draft.adminEmail || null,
          fleetAdminName: draft.adminName || null,
          fleetAdminPhone: draft.adminPhone || null,
          legalName: draft.legalName || null,
          name: draft.organizationName || null,
          region: draft.operatingRegions || null,
          type: "fleet_partner",
        },
        organizationId:
          draft.masterPersistence?.organizationId
          ?? draft.activeOrganizationId
          ?? search.organizationId
          ?? null,
        runId: resolvedRunId,
        rows: preview?.rows ?? [],
        units: draft.units,
        vertical: selectedVertical,
      })
      setSubmitResult(result)
      setDraft((previous) => ({
        ...previous,
        activeRunId: result.runId || previous.activeRunId,
        importBridge: {
          ...(previous.importBridge || createInitialFleetImportBridgeDraft()),
          dryRunCreated: result.acceptedRows,
          dryRunFailed: result.failedRows,
          dryRunRows: result.rowResults.map((row) => ({
            error: row.errors.length ? row.errors.join(" ") : null,
            plateNumber: row.assetNumber || null,
            rowIndex: row.rowIndex,
            status: row.status,
            tag: null,
          })),
          dryRunTotal: result.totalRows,
          dryRunUpdated: 0,
          lastDryRunAt: result.uploadedAt,
          sourceRows: preview?.rows ?? [],
        },
      }))
    } catch (error) {
      setSubmitError(
        isApiError(error)
          ? error.message
          : error instanceof Error
            ? error.message
            : "Fleet upload validation failed."
      )
    } finally {
      setIsSavingUpload(false)
    }
  }

  return (
    <FleetB2BPageShell
      activeSection="onboarding"
      title="Workbook import and validation"
      description="Upload the enterprise onboarding file, validate master-data quality, and save eligible vehicle rows into the active Fleet onboarding run."
      highlights={[
        "CSV bridge validation, header checks, row preview, and blocking-error review.",
        "Preview counts for assets, inferred people, assignments, and operating-unit hints.",
        "Persist validated upload rows to the onboarding run with row-level pass/fail outcomes.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorPanel
          title="Onboarding progress"
          description="Phase 3 activates the import surface on top of the Fleet B2B route scaffold."
        >
          <FleetOnboardingStepper activeStep="import" />
        </OperatorPanel>

        <OperatorStatusMessage>
          Current upload mode: <strong>CSV fleet onboarding contract</strong>. This step validates
          row-level quality and saves accepted vehicles into the active onboarding run before review.
        </OperatorStatusMessage>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
          <OperatorPanel
            title="Bridge import file"
            description="Use a CSV export from the asset master or build from the starter template below. This bridge import is organization-scoped and derives the organization from the current Fleet session."
          >
            <div className="grid gap-4">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Active onboarding context
                </div>
                <div className="mt-2 text-sm leading-6 text-muted-foreground">
                  Vertical: <strong>{FLEET_VERTICAL_LABELS[selectedVertical]}</strong>
                  <br />
                  Draft organization:{" "}
                  <strong>{draft.organizationName.trim() || "Not captured yet"}</strong>
                  <br />
                  Operating units drafted: <strong>{summary.totalUnits}</strong>
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
                <div className="text-sm font-medium">Supported bridge files</div>
                <div className="mt-2 text-sm leading-6 text-muted-foreground">
                  This Phase 3 slice accepts {BRIDGE_SUPPORTED_EXTENSIONS.join(", ")} files for the
                  legacy Fleet roster bridge. Multi-sheet `.xlsx` workbook parsing will land with
                  the dedicated B2B backend import APIs.
                </div>
              </div>

              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Choose CSV file</span>
                <input
                  type="file"
                  accept=".csv,.txt"
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  onChange={(event) => {
                    void handleFileSelection(event.target.files?.[0] ?? null)
                  }}
                />
              </label>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Starter CSV template</div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Copy this starter row into Excel or a CSV editor, then upload the saved file for
                  validation.
                </p>
                <pre className="mt-3 overflow-x-auto rounded-2xl border border-border/60 bg-background px-3 py-3 text-xs leading-6 text-muted-foreground">
{buildBridgeCsvTemplate(selectedVertical)}
                </pre>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void saveUploadRows()}
                  disabled={isSavingUpload || isParsing || !preview || preview.errors.length > 0}
                  className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                >
                  {isSavingUpload ? "Saving upload rows…" : "Validate and save upload"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFileName(null)
                    setCsvText("")
                    setPreview(null)
                    setSubmitResult(null)
                    setSubmitError(null)
                    setDraft((previous) => ({
                      ...previous,
                      importBridge: null,
                      kitIssuance: null,
                      masterPersistence: null,
                    }))
                  }}
                  className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                >
                  Clear file
                </button>
              </div>
            </div>
          </OperatorPanel>

          <div className="grid gap-5">
            <OperatorPanel
              title="Validation posture"
              description="Local validation happens before backend upload persistence so operators see blocking issues early."
            >
              {!preview ? (
                <OperatorEmptyState
                  title="No CSV loaded yet."
                  message="Choose a bridge CSV file to preview headers, row counts, and blocking issues."
                />
              ) : isParsing ? (
                <div className="text-sm text-muted-foreground">Parsing CSV file…</div>
              ) : (
                <div className="grid gap-3">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Selected file</div>
                    <div className="mt-2 text-lg font-semibold">
                      {selectedFileName || "Unnamed file"}
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-sm text-muted-foreground">Blocking errors</div>
                      <div className="mt-2 text-2xl font-semibold">{preview.errors.length}</div>
                    </div>
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-sm text-muted-foreground">Warnings</div>
                      <div className="mt-2 text-2xl font-semibold">{preview.warnings.length}</div>
                    </div>
                  </div>

                  {preview.errors.length > 0 ? (
                    <OperatorStatusMessage tone="danger">
                      {preview.errors.join(" ")}
                    </OperatorStatusMessage>
                  ) : (
                    <OperatorStatusMessage>
                      Local CSV validation passed. You can validate and save rows to the onboarding run.
                    </OperatorStatusMessage>
                  )}

                  {preview.warnings.length > 0 ? (
                    <OperatorStatusMessage tone="warning">
                      {preview.warnings.join(" ")}
                    </OperatorStatusMessage>
                  ) : null}

                  {submitError ? (
                    <OperatorStatusMessage tone="danger">{submitError}</OperatorStatusMessage>
                  ) : null}
                </div>
              )}
            </OperatorPanel>

            <OperatorPanel
              title="What this bridge does not do yet"
              description="This keeps the operator expectations aligned with the actual backend state."
            >
              <div className="grid gap-2 text-sm text-muted-foreground">
                <div className="rounded-2xl border border-border/60 px-3 py-2">
                  No `.xlsx` multi-sheet parsing yet
                </div>
                <div className="rounded-2xl border border-border/60 px-3 py-2">
                  Upload does not write Fleet masters directly; review persistence remains the cutover point
                </div>
                <div className="rounded-2xl border border-border/60 px-3 py-2">
                  Upload only saves validated rows; invalid rows stay blocked until corrected
                </div>
              </div>
            </OperatorPanel>
          </div>
        </section>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          <OperatorPanel
            title="Local preview summary"
            description="Preview the CSV through the Fleet B2B lens before backend row-level validation and onboarding-run persistence."
          >
            {!preview || !previewSummary ? (
              <OperatorEmptyState
                title="No preview generated yet."
                message="Upload a CSV bridge file to see inferred asset, people, assignment, and operating-unit counts."
              />
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  <div className="text-sm text-muted-foreground">Assets</div>
                  <div className="mt-2 text-2xl font-semibold">{previewSummary.assetCount}</div>
                </div>
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  <div className="text-sm text-muted-foreground">Operating-unit hints</div>
                  <div className="mt-2 text-2xl font-semibold">
                    {previewSummary.operatingUnitCount}
                  </div>
                </div>
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  <div className="text-sm text-muted-foreground">People inferred</div>
                  <div className="mt-2 text-2xl font-semibold">{previewSummary.peopleCount}</div>
                </div>
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  <div className="text-sm text-muted-foreground">Assignments inferred</div>
                  <div className="mt-2 text-2xl font-semibold">
                    {previewSummary.assignmentCount}
                  </div>
                </div>
              </div>
            )}

            {preview?.headers.length ? (
              <div className="mt-4 grid gap-3">
                <div className="text-sm font-medium">Detected headers</div>
                <div className="flex flex-wrap gap-2">
                  {preview.headers.map((header) => (
                    <span
                      key={header}
                      className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs text-muted-foreground"
                    >
                      {header}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </OperatorPanel>

          <OperatorPanel
            title="Saved upload results"
            description="Backend validation stores eligible rows in the onboarding run and returns row-level failures for correction."
          >
            {!submitResult ? (
              <OperatorEmptyState
                title="No saved upload result yet."
                message="Validate and save upload rows to inspect accepted and failed row outcomes."
              />
            ) : (
              <div className="grid gap-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Total rows</div>
                    <div className="mt-2 text-2xl font-semibold">{submitResult.totalRows}</div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Accepted rows</div>
                    <div className="mt-2 text-2xl font-semibold">{submitResult.acceptedRows}</div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Eligible vehicles</div>
                    <div className="mt-2 text-2xl font-semibold">{submitResult.validRows.length}</div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Failed</div>
                    <div className="mt-2 text-2xl font-semibold">{submitResult.failedRows}</div>
                  </div>
                </div>

                <div className="grid gap-3">
                  {submitResult.rowResults.slice(0, 8).map((row) => (
                    <div
                      key={`${row.rowIndex}-${row.assetNumber || row.status}`}
                      className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">
                            Row {row.rowIndex}: {row.assetNumber || "Missing vehicle identifier"}
                          </div>
                          <div className="mt-2 text-sm leading-6 text-muted-foreground">
                            {row.errors.length > 0
                              ? row.errors.join(" ")
                              : `Accepted vehicle ${row.assetNumber || "row"} for onboarding run persistence.`}
                          </div>
                        </div>
                        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                          {row.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </OperatorPanel>
        </section>

        <OperatorPanel
          title="Next action"
          description="A valid saved upload should hand off into review so Fleet can apply blocking and readiness gates before kits."
        >
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onContinueToReview}
              disabled={!canContinueToReview}
              className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue to review
            </button>
          </div>

          {canContinueToReview ? (
            <OperatorStatusMessage>
              The import bridge draft is clean enough to move into onboarding review.
            </OperatorStatusMessage>
          ) : (
            <OperatorStatusMessage tone="warning">
              Continue to review unlocks only after upload validation is saved without blocking errors or failed rows.
            </OperatorStatusMessage>
          )}
        </OperatorPanel>
      </div>
    </FleetB2BPageShell>
  )
}
